import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { saveAs } from 'file-saver';

import { createAppThunk } from '@mse-reg/app/store';
import { IExcelTemplateClient } from '@mse-reg/clients';
import { FileInfo, loadFileAsync } from '@mse-reg/util';
import { addSuccess } from '../notification.slice';

const initialState = {
  loading: false as boolean,
  registrationTemplate: null as FileInfo,
  summaryTemplate: null as FileInfo,
  examTemplate: null as FileInfo,
};

export type AuthState = typeof initialState;

const downloadRegistrationTemplate = createAppThunk(
  'template/download-registration-template',
  async (_, deps: { templateClient: IExcelTemplateClient }) => {
    const fileResponse = await deps.templateClient.download(1);
    saveAs(fileResponse.data, fileResponse.fileName);
    return fileResponse;
  },
  (r) => ({ templateClient: r.resolve<IExcelTemplateClient>('IExcelTemplateClient') }),
);

const downloadSummaryTemplate = createAppThunk(
  'template/download-summary-template',
  async (_, deps: { templateClient: IExcelTemplateClient }) => {
    const fileResponse = await deps.templateClient.download(2);
    saveAs(fileResponse.data, fileResponse.fileName);
    return fileResponse;
  },
  (r) => ({ templateClient: r.resolve<IExcelTemplateClient>('IExcelTemplateClient') }),
);

const uploadRegistrationTemplate = createAppThunk(
  'template/upload-registration-template',
  async (fileInfo: FileInfo, deps: { templateClient: IExcelTemplateClient }, dispatch) => {
    const file = await loadFileAsync(fileInfo);
    await deps.templateClient.upload(1, {
      data: file,
      fileName: file?.name,
    });
    dispatch(templateActions.resetRegistrationTemplateSelection());
    dispatch(addSuccess('Successfully updated the registration template'));
  },
  (r) => ({ templateClient: r.resolve<IExcelTemplateClient>('IExcelTemplateClient') }),
);

const uploadSummaryTemplate = createAppThunk(
  'template/upload-summary-template',
  async (fileInfo: FileInfo, deps: { templateClient: IExcelTemplateClient }, dispatch) => {
    const file = await loadFileAsync(fileInfo);
    await deps.templateClient.upload(2, {
      data: file,
      fileName: file?.name,
    });
    dispatch(templateActions.resetSummaryTemplateSelection());
    dispatch(addSuccess('Successfully updated the summary template'));
  },
  (r) => ({ templateClient: r.resolve<IExcelTemplateClient>('IExcelTemplateClient') }),
);

const downloadExamTemplate = createAppThunk(
  'template/download-exam-template',
  async (_, deps: { templateClient: IExcelTemplateClient }) => {
    const fileResponse = await deps.templateClient.download(3);
    saveAs(fileResponse.data, fileResponse.fileName);
    return fileResponse;
  },
  (r) => ({ templateClient: r.resolve<IExcelTemplateClient>('IExcelTemplateClient') }),
);

const uploadExamTemplate = createAppThunk(
  'template/upload-exam-template',
  async (fileInfo: FileInfo, deps: { templateClient: IExcelTemplateClient }, dispatch) => {
    const file = await loadFileAsync(fileInfo);
    await deps.templateClient.upload(3, {
      data: file,
      fileName: file?.name,
    });
    dispatch(templateActions.resetExamTemplateSelection());
    dispatch(addSuccess('Successfully updated the exam registration template'));
  },
  (r) => ({ templateClient: r.resolve<IExcelTemplateClient>('IExcelTemplateClient') }),
);

const templateSlice = createSlice({
  name: 'template',
  initialState,
  reducers: {
    resetRegistrationTemplateSelection(state) {
      state.registrationTemplate = null;
    },
    selectRegistrationTemplate(state, action: PayloadAction<FileInfo>) {
      state.registrationTemplate = action.payload;
    },
    resetSummaryTemplateSelection(state) {
      state.summaryTemplate = null;
    },
    selectSummaryTemplate(state, action: PayloadAction<FileInfo>) {
      state.summaryTemplate = action.payload;
    },
    resetExamTemplateSelection(state) {
      state.examTemplate = null;
    },
    selectExamTemplate(state, action: PayloadAction<FileInfo>) {
      state.examTemplate = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(downloadRegistrationTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(downloadRegistrationTemplate.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadRegistrationTemplate.rejected, (state) => {
        state.loading = false;
      })
      .addCase(uploadRegistrationTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadRegistrationTemplate.fulfilled, (state) => {
        state.loading = false;
        state.registrationTemplate = null;
      })
      .addCase(uploadRegistrationTemplate.rejected, (state) => {
        state.loading = false;
      })
      .addCase(downloadSummaryTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(downloadSummaryTemplate.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadSummaryTemplate.rejected, (state) => {
        state.loading = false;
      })
      .addCase(uploadSummaryTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadSummaryTemplate.fulfilled, (state) => {
        state.loading = false;
        state.summaryTemplate = null;
      })
      .addCase(uploadSummaryTemplate.rejected, (state) => {
        state.loading = false;
      })
      .addCase(downloadExamTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(downloadExamTemplate.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadExamTemplate.rejected, (state) => {
        state.loading = false;
      })
      .addCase(uploadExamTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadExamTemplate.fulfilled, (state) => {
        state.loading = false;
        state.examTemplate = null;
      })
      .addCase(uploadExamTemplate.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const templateReducer = templateSlice.reducer;
export const templateActions = templateSlice.actions;
export const templateThunks = {
  downloadRegistrationTemplate,
  downloadSummaryTemplate,
  uploadSummaryTemplate,
  uploadRegistrationTemplate,
  downloadExamTemplate,
  uploadExamTemplate,
};
